import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaClock } from 'react-icons/fa';
import { searchService, SearchResultAccount, SearchResultTeam, RecentSearchItem } from '../services/searchService';
import { baseURL } from '../Constants';
import '../styles/components/search-bar.scss';

interface SearchBarProps {
  isMobile?: boolean;
  onClose?: () => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ isMobile = false, onClose }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [players, setPlayers] = useState<SearchResultAccount[]>([]);
  const [teams, setTeams] = useState<SearchResultTeam[]>([]);
  const [recentSearches, setRecentSearches] = useState<RecentSearchItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const searchBarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  // Load recent searches on mount
  useEffect(() => {
    setRecentSearches(searchService.getRecentSearches());
  }, []);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setPlayers([]);
      setTeams([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await searchService.searchAll(searchQuery);
      setPlayers(results.players.slice(0, 5)); // Limit to 5 results
      setTeams(results.teams.slice(0, 5)); // Limit to 5 results
      setSelectedIndex(-1); // Reset selection
    } catch (error) {
      console.error('Search error:', error);
      setPlayers([]);
      setTeams([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle query change with debouncing
  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      performSearch(value);
    }, 300); // 300ms debounce
  };

  // Handle input focus
  const handleFocus = () => {
    setIsOpen(true);
    if (!query) {
      setRecentSearches(searchService.getRecentSearches());
    }
  };

  // Clear search
  const handleClear = () => {
    setQuery('');
    setPlayers([]);
    setTeams([]);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Navigate to player profile
  const navigateToPlayer = (steamId: string, playerName: string, teamName?: string) => {
    searchService.addRecentSearch({
      type: 'player',
      id: steamId,
      name: playerName,
      teamName: teamName
    });
    navigate(`/profile/${steamId}`);
    setIsOpen(false);
    setQuery('');
    if (onClose) onClose();
  };

  // Navigate to team page
  const navigateToTeam = (teamId: string, teamName: string, season: number | null) => {
    searchService.addRecentSearch({
      type: 'team',
      id: teamId,
      name: teamName,
      season: season
    });
    const url = season ? `/teams/${teamId}?season=${season}` : `/teams/${teamId}`;
    navigate(url);
    setIsOpen(false);
    setQuery('');
    if (onClose) onClose();
  };

  // Handle recent search click - navigate directly
  const handleRecentSearchClick = (item: RecentSearchItem) => {
    if (item.type === 'player') {
      navigateToPlayer(item.id, item.name, item.teamName);
    } else {
      navigateToTeam(item.id, item.name, item.season || null);
    }
  };

  // Calculate total results for keyboard navigation
  const totalResults = players.length + teams.length + (query === '' ? recentSearches.length : 0);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (totalResults === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < totalResults - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();

      // Handle selection based on index
      if (query === '' && recentSearches.length > 0) {
        // Recent searches - navigate directly
        if (selectedIndex < recentSearches.length) {
          const item = recentSearches[selectedIndex];
          handleRecentSearchClick(item);
        }
      } else {
        // Search results
        if (selectedIndex < players.length) {
          // Navigate to player
          const player = players[selectedIndex];
          navigateToPlayer(player.steamID, player.playerName, player.team?.name);
        } else if (selectedIndex < players.length + teams.length) {
          // Navigate to team
          const teamIndex = selectedIndex - players.length;
          const team = teams[teamIndex];
          navigateToTeam(team.id, team.name, team.currentSeason);
        }
      }
    }
  };

  // Get avatar URL for player
  const getPlayerAvatar = (steamId: string) => {
    return `${baseURL}/Account/${steamId}/avatar`;
  };

  // Get team logo URL
  const getTeamLogo = (teamId: string) => {
    return `${baseURL}/Team/${teamId}/logo`;
  };

  // Fallback avatar SVG
  const fallbackAvatar = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ccircle cx='50' cy='40' r='20' fill='%23666'/%3E%3Cpath d='M 20 85 Q 20 60 50 60 Q 80 60 80 85 Z' fill='%23666'/%3E%3C/svg%3E";

  // Fallback team logo SVG
  const fallbackTeamLogo = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='40' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff' font-weight='bold'%3E%3F%3C/text%3E%3C/svg%3E";

  // Show dropdown when there are results or recent searches
  const showDropdown = isOpen && (query.length >= 2 || (!query && recentSearches.length > 0));

  return (
    <div className={`search-bar ${isMobile ? 'mobile' : ''}`} ref={searchBarRef}>
      <div className="search-input-container">
        <FaSearch className="search-icon" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search players or teams..."
          value={query}
          onChange={handleQueryChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          className="search-input"
        />
        {query && (
          <button className="clear-button" onClick={handleClear} aria-label="Clear search">
            <FaTimes />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="search-dropdown">
          {isLoading && (
            <div className="search-loading">
              <div className="spinner"></div>
              Searching...
            </div>
          )}

          {!isLoading && query === '' && recentSearches.length > 0 && (
            <div className="search-section">
              <div className="section-header">
                <FaClock /> Recent
              </div>
              <ul className="search-results">
                {(isMobile ? recentSearches.slice(0, 3) : recentSearches).map((item, index) => (
                  <li
                    key={`${item.type}-${item.id}`}
                    className={`search-result recent-search ${selectedIndex === index ? 'selected' : ''}`}
                    onClick={() => handleRecentSearchClick(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRecentSearchClick(item); }}
                  >
                    {item.type === 'player' ? (
                      <img
                        src={getPlayerAvatar(item.id)}
                        alt={item.name}
                        className="result-avatar"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          if (!img.src.includes('data:image')) {
                            img.src = fallbackAvatar;
                          }
                        }}
                      />
                    ) : (
                      <img
                        src={getTeamLogo(item.id)}
                        alt={item.name || 'Team'}
                        className="result-avatar team-logo"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          if (!img.src.includes('data:image')) {
                            img.src = fallbackTeamLogo;
                          }
                        }}
                      />
                    )}
                    <div className="result-info">
                      <div className="result-name">{item.name}</div>
                      {item.teamName && (
                        <div className="result-meta">{item.teamName}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isLoading && query.length >= 2 && (
            <>
              {/* Players Section */}
              {players.length > 0 && (
                <div className="search-section">
                  <div className="section-header">Players</div>
                  <ul className="search-results">
                    {players.map((player, index) => (
                      <li
                        key={player.steamID}
                        className={`search-result ${selectedIndex === index ? 'selected' : ''}`}
                        onClick={() => navigateToPlayer(player.steamID, player.playerName, player.team?.name)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigateToPlayer(player.steamID, player.playerName, player.team?.name); }}
                      >
                        <img
                          src={getPlayerAvatar(player.steamID)}
                          alt={player.playerName}
                          className="result-avatar"
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            if (!img.src.includes('data:image')) {
                              img.src = fallbackAvatar;
                            }
                          }}
                        />
                        <div className="result-info">
                          <div className="result-name">{player.playerName}</div>
                          {player.team && (
                            <div className="result-meta">{player.team.name}</div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Teams Section */}
              {teams.length > 0 && (
                <div className="search-section">
                  <div className="section-header">Teams</div>
                  <ul className="search-results">
                    {teams.map((team, index) => (
                      <li
                        key={team.id}
                        className={`search-result ${selectedIndex === players.length + index ? 'selected' : ''}`}
                        onClick={() => navigateToTeam(team.id, team.name, team.currentSeason)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigateToTeam(team.id, team.name, team.currentSeason); }}
                      >
                        <img
                          src={getTeamLogo(team.id)}
                          alt={team.name}
                          className="result-avatar team-logo"
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            if (!img.src.includes('data:image')) {
                              img.src = fallbackTeamLogo;
                            }
                          }}
                        />
                        <div className="result-info">
                          <div className="result-name">{team.name}</div>
                          {team.currentDivision && team.currentSeason && (
                            <div className="result-meta">
                              {team.currentDivision} - Season {team.currentSeason}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* No results */}
              {players.length === 0 && teams.length === 0 && (
                <div className="no-results">
                  No players or teams found for "{query}"
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
